import { useMemo, useState } from 'react'

import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import {
  Alert,
  Button,
  Modal,
  Radio,
  Textarea,
  TextInput
} from 'flowbite-react'
import { useQueryClient } from 'react-query'
import Select from 'react-select'
import { toast } from 'react-toastify'

import {
  bancoOptions,
  tipoChavePixOptions,
  tipoContaOptions
} from './constants'
import toastService from '@/components/toast/toast-service'
import { QueryKey } from '@/configs/queryKeys'
import { useDevolucoesContext } from '@/contexts/devolucoes.context'
import { useGetUserData } from '@/hooks/queries/use-get-user-data'
import { tratarDevolucao } from '@/services/devolucoes'
import { CurrencyFormat } from '@/utils/currency-format'
import { cpfMask, pixCnpjMask, pixPhoneMask, pixRandomKeyMask, unmaskPixKey } from '@/utils/masks'

interface ITratativasDevolucoesModalProps {
  onClose: () => void
  isOpen: boolean
}




const aplicarMascaraChavePix = (
  chavePix?: string,
  tipoChavePix?: string
): string | undefined => {
  if (chavePix === undefined || chavePix === null) {
    return undefined
  }

  if (tipoChavePix === 'CPF') {
    return cpfMask(chavePix)
  }

  if (tipoChavePix === 'CNPJ') {
    return pixCnpjMask(chavePix)
  }

  if (tipoChavePix === 'Chave Aleatória') {
    return pixRandomKeyMask(chavePix)
  }

  if (tipoChavePix === 'Telefone') {
    return pixPhoneMask(chavePix)
  }

  return chavePix
}

const normalizarTipoChavePix = (tipoChavePix?: string | number | null): string | undefined => {
  if (tipoChavePix === undefined || tipoChavePix === null) {
    return undefined
  }

  const tipoChavePixNormalizado = String(tipoChavePix).trim()

  if (tipoChavePixNormalizado === '') {
    return undefined
  }

  if (tipoChavePixOptions.some(option => option.value === tipoChavePixNormalizado)) {
    return tipoChavePixNormalizado
  }

  const indiceTipoChavePix = Number(tipoChavePixNormalizado)

  if (
    Number.isInteger(indiceTipoChavePix) &&
    indiceTipoChavePix >= 0 &&
    indiceTipoChavePix < tipoChavePixOptions.length
  ) {
    return tipoChavePixOptions[indiceTipoChavePix].value
  }

  return tipoChavePixNormalizado
}

const TratativasDevolucoesModal: React.FC<ITratativasDevolucoesModalProps> = ({
                                                                                onClose,
                                                                                isOpen
                                                                              }: ITratativasDevolucoesModalProps) => {
  const { dadosBancarios, devolucaoSelecionada } = useDevolucoesContext()
  const { data: userData } = useGetUserData()
  const queryClient = useQueryClient()
  const [processing, setProcessing] = useState<boolean>(false)

  const [erros, setErros] = useState<{ campo: string; erro: string }[]>()

  const [tipoTratativa, setTipoTratativa] = useState<string>('')

  const [novoBanco, setNovoBanco] = useState<string>()
  const selectedBancoOption = bancoOptions?.find(
    option => option.value === novoBanco
  )
  const [novoTipoConta, setNovoTipoConta] = useState<string>()
  const selectedTipoContaOption = tipoContaOptions?.find(
    option => option.value === novoTipoConta
  )
  const [novaAgencia, setNovaAgencia] = useState<string>()
  const [novoAgenciaDig, setNovoAgenciaDig] = useState<string>()
  const [novaConta, setNovaConta] = useState<string>()
  const [novoContaDig, setNovoContaDig] = useState<string>()
  const [novaChavePix, setNovoChavePix] = useState<string>('')
  const [novaTipoChavePix, setNovoTipoChavePix] = useState<string | undefined>(undefined)
  const selectedTipoChavePixOption = tipoChavePixOptions?.find(
    option => option.value === novaTipoChavePix
  )

  const [observacao, setObservacao] = useState<string>()
  const chavePixReadonly = novaTipoChavePix === 'CPF'
  const chavePixMascarada = aplicarMascaraChavePix(
    novaChavePix,
    novaTipoChavePix
  )

  const devolucaoEmTratativa = useMemo(() => {
    const item = { dadosBancarios, devolucaoSelecionada }
    setNovoBanco(item?.dadosBancarios?.numeroDoBanco)
    setNovoTipoConta(item?.dadosBancarios?.tipoDeContaBancaria)
    setNovaAgencia(item?.dadosBancarios?.agenciaBancaria)
    setNovoAgenciaDig(item?.dadosBancarios?.digitoDaAgenciaBancaria)
    setNovaConta(item?.dadosBancarios?.contaBancaria)
    setNovoContaDig(item?.dadosBancarios?.digitoDaContaBancaria)
    const tipoChavePix = normalizarTipoChavePix(item?.dadosBancarios?.tipoChavePix)
    const chavePixInicial =
      tipoChavePix === 'CPF'
        ? unmaskPixKey(item?.devolucaoSelecionada?.cpf, 'CPF')
        : unmaskPixKey(item?.dadosBancarios?.chavePix, tipoChavePix)
    setNovoChavePix(chavePixInicial ?? '')
    setNovoTipoChavePix(tipoChavePix)
    setObservacao('')
    setTipoTratativa('')
    return item
  }, [devolucaoSelecionada, dadosBancarios])

  const validacaoForm = () => {
    const erros = []
    const campoPreenchido = (valor?: string | null) =>
      valor !== undefined && valor !== null && String(valor).trim() !== ''

    if (tipoTratativa === undefined || tipoTratativa === '') {
      erros.push({
        campo: 'tratativa',
        erro: 'Uma tratativa deve ser selecionada'
      })
      return erros
    }

    if (tipoTratativa === 'Creditar') {
      const tipoChavePixPreenchido = campoPreenchido(novaTipoChavePix)
      const chavePixPreenchida = campoPreenchido(novaChavePix)

      if (tipoChavePixPreenchido && !chavePixPreenchida) {
        erros.push({
          campo: 'chavePix',
          erro: 'Chave Pix deve ser preenchida quando o Tipo Chave Pix for informado.'
        })

        return erros
      }

      if (!tipoChavePixPreenchido && chavePixPreenchida) {
        erros.push({
          campo: 'tipoChavePix',
          erro: 'Tipo Chave Pix deve ser selecionado quando a Chave Pix for informada.'
        })

        return erros
      }

      if (novaTipoChavePix === 'Telefone') {
        const chavePixTelefone = unmaskPixKey(novaChavePix, novaTipoChavePix)

        if (String(chavePixTelefone ?? '').length !== 13) {
          erros.push({
            campo: 'chavePix',
            erro: 'A Chave Pix Telefone deve estar no formato +5599999999999.'
          })

          return erros
        }
      }

      if (novaTipoChavePix === 'CPF') {
        const chavePixCpfMascarada = cpfMask(novaChavePix ?? '')

        if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(chavePixCpfMascarada)) {
          erros.push({
            campo: 'chavePix',
            erro: 'A Chave Pix CPF deve estar no formato 999.999.999-99.'
          })

          return erros
        }
      }

      if (novaTipoChavePix === 'CNPJ') {
        const chavePixCnpjMascarada = pixCnpjMask(novaChavePix ?? '')
       
        if (!/^[A-Za-z0-9]{2}\.[A-Za-z0-9]{3}\.[A-Za-z0-9]{3}\/[A-Za-z0-9]{4}-[A-Za-z0-9]{2}$/.test(chavePixCnpjMascarada)) {
          erros.push({
          campo: 'chavePix',
          erro: 'A Chave Pix CNPJ deve estar no formato XX.XXX.XXX/XXXX-XX.'
        })

          return erros
        }
      }

      

      if (novaTipoChavePix === 'E-mail') {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((novaChavePix ?? '').trim())) {
          erros.push({
            campo: 'chavePix',
            erro: 'A Chave Pix E-mail deve ser um endereço de e-mail válido.'
          })

          return erros
        }
      }

      if (novaTipoChavePix === 'Chave Aleatória') {
        const chavePixAleatoriaMascarada = pixRandomKeyMask(novaChavePix ?? '')

        if (!/^[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{12}$/i.test(chavePixAleatoriaMascarada)) {
          erros.push({
            campo: 'chavePix',
            erro: 'A Chave Pix Aleatória deve estar no formato UUID: 99999999-9999-9999-9999-999999999999.'
          })
          return erros
        }
      }

      const dadosBancariosCompletos =
        campoPreenchido(novoBanco) &&
        campoPreenchido(novoTipoConta) &&
        campoPreenchido(novaAgencia) &&
        campoPreenchido(novaConta) &&
        campoPreenchido(novoContaDig)

      const dadosPixCompletos =
        tipoChavePixPreenchido && chavePixPreenchida

      if (!dadosBancariosCompletos && !dadosPixCompletos) {
        const erroDadosCredito =
          'Informe Tipo Chave Pix e Chave Pix ou preencha os dados bancários completos (Banco, Agência, Tipo Conta, Dígitos e Conta)'
        erros.push({ campo: 'tipoChavePix', erro: erroDadosCredito })

        return erros
      }

      const bancosComContaMaxCincoDigitos = ['341']
      const bancoSelecionado = String(novoBanco ?? '')
      const contaNumerica = String(novaConta ?? '').replace(/\D/g, '')

      if (!dadosPixCompletos && bancosComContaMaxCincoDigitos.includes(bancoSelecionado) && contaNumerica.length !== 5) {
        erros.push({
          campo: 'conta',
          erro: 'O banco informado aceita somente 5 dígitos na conta bancária. Se a conta começar com zeros à esquerda (00...), remova os zeros até ficar com no máximo 5 dígitos.'
        })
      }

      return erros
    }

    if (tipoTratativa === 'Reprocessar' && (observacao === undefined || observacao === '')) {
      erros.push({
        campo: 'observacao',
        erro: 'Observação deve ser preenchida'
      })
    }

    return erros
  }

  const handleTratarDevolucao = () => {
    setProcessing(true)
    const validacao = validacaoForm()
    setErros(validacao)
    if (validacao?.length === 0) {
      const chavePixSemMascara = unmaskPixKey(novaChavePix, novaTipoChavePix)
      const chavePixParaEnvio =
        novaTipoChavePix === 'Chave Aleatória' || novaTipoChavePix === 'Telefone'
          ? aplicarMascaraChavePix(novaChavePix, novaTipoChavePix)
          : chavePixSemMascara
      const tratativa =
        tipoTratativa === 'Creditar'
          ? 'DepositoBancario'
          : tipoTratativa === 'Descontar'
            ? 'DescontarNaProximaFatura'
            : 'ReprocessarCredito'
      const DTO = {
        idPedidoVenda: devolucaoEmTratativa.devolucaoSelecionada?.idPedido,
        idVenda: devolucaoEmTratativa.devolucaoSelecionada?.idVenda,
        idItemVenda: devolucaoEmTratativa.devolucaoSelecionada?.id,
        atualizadoPor: userData?.userId,
        atualizadoPorNome: userData?.userName,
        tratativa: tratativa,
        dadosBancariosDoTrabalhador: {
          numeroBanco: novoBanco,
          nomeBanco: bancoOptions?.find(option => option.value === novoBanco)
            ?.label,
          tipoConta: novoTipoConta,
          agencia: novaAgencia,
          digitoAgencia: novoAgenciaDig,
          conta: novaConta,
          digitoConta: novoContaDig,
          chavePix: chavePixParaEnvio,
          tipoChavePix: normalizarTipoChavePix(novaTipoChavePix),
        },
        reprocessarCredito: {
          observacao: observacao
        }
      }
      tratarDevolucao(DTO)
        .then(() => {
          toast(CustomNotification, {
            autoClose: 20000,
          })
          setProcessing(false)
          queryClient.invalidateQueries(QueryKey.devolucoesList)
          onClose()
        })
        .catch(() => {
          setProcessing(false)
          toastService.error(
            'Ocorreu um erro ao utilizar tratativa selecionada!'
          )
        })
    } else {
      setProcessing(false)
      toastService.error('A tratativa da devolução contêm erros')
    }
  }

  const CustomNotification = () => {
    return (
      <div className="flex flex-col w-full">
        <div className='flex items-center'>
          <CheckIcon className='h-6 w-9 text-white bg-quaternary rounded-full m-2' title='Sucesso' />
          <p className="text-black font-bold">
            Reprocessamento concluído com sucesso!
          </p>
        </div>
        <p className="text-m text-zinc-400">Acompanhe tudo pelo <span className="font-semibold">Histórico.</span></p>

        <p className="text-m text-zinc-400 pt-5">Caso o item ainda esteja com status <span className="font-semibold">Aberto</span>, é necessário:</p>
        <div className="text-s text-zinc-400">
          <ol className="list-decimal pl-5">
            <li><span className="font-semibold">Verificar o motivo</span> que está inviabilizando a compra</li>
            <li><span className="font-semibold">Corrigir a inconsistência</span> ou</li>
            <li><span className="font-semibold">Selecionar outra tratativa</span></li>
          </ol>
        </div>
        <p className="text-m text-zinc-400 pt-5">Clique no botão <span className="font-semibold">Motivo da Devolução</span> para verificar a inconsistência.</p>
      </div>
    )
  }

  return (
    <Modal
      popup
      show={isOpen}
      onClose={onClose}
      className='flex items-center'
      size='7xl'
    >
      <Modal.Header>
        <p className='text-center font-semibold p-3 text-quaternary text-2xl'>
          Tratativas de Devolução
        </p>
      </Modal.Header>
      <Modal.Body>
        <span className='pb-3 mx-3 text-xl flex'>
          {devolucaoEmTratativa?.devolucaoSelecionada?.nome}
        </span>
        <span className='pb-3 mx-3 text-m flex'>
          {devolucaoEmTratativa?.devolucaoSelecionada?.codigoDoBeneficio +
            ' - ' +
            devolucaoEmTratativa?.devolucaoSelecionada?.descricaoBeneficio}
        </span>
        <div
          className={
            tipoTratativa === 'Descontar'
              ? 'grid grid-cols-12 bg-white rounded-xl gap-5 md:gap-0 p-5 mx-5 mb-5 border border-quaternary'
              : 'grid grid-cols-12 bg-white rounded-xl gap-5 md:gap-0 p-5 mx-5 mb-5 border'
          }
        >
          <div className='p-3 flex md:col-span-12 md:text-left gap-4'>
            <Radio
              checked={tipoTratativa === 'Descontar'}
              onClick={() => setTipoTratativa('Descontar')}
              value='Descontar'
              name='radio-descontar'
              className='text-quaternary focus:ring-quaternary'
              data-testid='radio-descontar'
            />
            <span className='text-quaternary font-semibold pb-3 text-xl'>
              Descontar na Próxima Fatura
            </span>
            {erros?.find(e => e.campo == 'tratativa') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='select-tratativa-erro'
              >
                {erros?.find(e => e.campo == 'tratativa')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div
            className='p-3 md:col-span-12  md:text-left'
            data-testid='descontar-info'
          >
            <p>
              Será lançado o valor de{' '}
              <b>
                {CurrencyFormat(
                  devolucaoEmTratativa.devolucaoSelecionada?.total ?? ''
                )}
              </b>{' '}
              como crédito da empresa para utilização em seu próximo pedido.
            </p>
          </div>
        </div>

        <div
          className={
            tipoTratativa === 'Creditar'
              ? 'grid grid-cols-12 bg-white rounded-xl gap-5 md:gap-0 p-5 mx-5 mb-5 border border-quaternary'
              : 'grid grid-cols-12 bg-white rounded-xl gap-5 md:gap-0 p-5 mx-5 mb-5 border'
          }
        >
          <div className='p-3 flex md:col-span-12 md:text-left gap-4'>
            <Radio
              checked={tipoTratativa === 'Creditar'}
              onClick={() => setTipoTratativa('Creditar')}
              value='Creditar'
              name='radio-creditar'
              className='text-quaternary focus:ring-quaternary'
              data-testid='radio-creditar'
            />
            <span className='text-quaternary font-semibold pb-3 text-xl'>
              Crédito em Conta do Beneficiário
            </span>
            {erros?.find(e => e.campo == 'tratativa') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='select-tratativa-erro'
              >
                {erros?.find(e => e.campo == 'tratativa')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div
            className='p-3 md:col-span-12 md:text-left'
            data-testid='creditar-info'
          >
            <p>
              Informe os dados da conta do beneficiário para depósito no valor
              de{' '}
              <b>
                {CurrencyFormat(
                  devolucaoEmTratativa.devolucaoSelecionada?.total ?? ''
                )}
              </b>
              .
            </p>
            <Alert color='warning' className='mt-3'>
              <div
                className='flex justify-center'
                data-testid='alerta-creditar'
              >
                <ExclamationTriangleIcon className='w-5 h-5 mr-2' />
                <span>
                  Atenção! NÃO INFORME CONTA-SALÁRIO, pois ela só aceita
                  créditos de salários
                </span>
              </div>
            </Alert>
          </div>

          <div
            className='p-3 col-span-6 text-left md:text-left'
            data-testid='select-banco'
          >
            <p>Banco:</p>
            <Select
              placeholder='Selecione um banco'
              isSearchable
              escapeClearsValue
              isClearable={true}
              noOptionsMessage={() => 'Sem resultados'}
              value={selectedBancoOption}
              onChange={selected => setNovoBanco(selected?.value)}
              options={bancoOptions}
            />
            {erros?.find(e => e.campo == 'banco') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='select-banco-erro'
              >
                {erros?.find(e => e.campo == 'banco')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div
            className='p-3 md:col-span-6 md:text-left'
            data-testid='select-tipo-conta'
          >
            <p>Tipo Conta:</p>
            <Select
              placeholder='Selecione o tipo de conta'
              isSearchable
              escapeClearsValue
              isClearable={true}
              noOptionsMessage={() => 'Sem resultados'}
              value={selectedTipoContaOption}
              onChange={selected => setNovoTipoConta(selected?.value)}
              options={tipoContaOptions}
            />
            {erros?.find(e => e.campo == 'tipoConta') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='select-tipoConta-erro'
              >
                {erros?.find(e => e.campo == 'tipoConta')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div className='p-3 md:col-span-2 md:text-left'>
            <p>Agência:</p>
            <TextInput
              type='number'
              color='light'
              defaultValue={
                devolucaoEmTratativa.dadosBancarios?.agenciaBancaria
              }
              value={novaAgencia}
              onChange={value => setNovaAgencia(value.target.value)}
              data-testid='input-agencia'
            />
            {erros?.find(e => e.campo == 'agencia') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='input-agencia-erro'
              >
                {erros?.find(e => e.campo == 'agencia')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div className='p-3 md:col-span-1 md:text-left'>
            <p>Dig:</p>
            <TextInput
              type='number'
              color='light'
              defaultValue={
                devolucaoEmTratativa.dadosBancarios?.digitoDaAgenciaBancaria
              }
              value={novoAgenciaDig}
              onChange={value => setNovoAgenciaDig(value.target.value)}
              data-testid='input-agencia-dig'
            />
          </div>

          <div className='p-3 md:col-span-2 md:text-left'>
            <p>Conta:</p>
            <TextInput
              type='number'
              color='light'
              defaultValue={devolucaoEmTratativa.dadosBancarios?.contaBancaria}
              value={novaConta}
              onChange={value => setNovaConta(value.target.value)}
              data-testid='input-conta'
            />
            {erros?.find(e => e.campo == 'conta') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='input-conta-erro'
              >
                {erros?.find(e => e.campo == 'conta')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div className='p-3 md:col-span-1 md:text-left'>
            <p>Dig:</p>
            <TextInput
              type='number'
              color='light'
              defaultValue={
                devolucaoEmTratativa.dadosBancarios?.digitoDaContaBancaria
              }
              value={novoContaDig}
              onChange={value => setNovoContaDig(value.target.value)}
              data-testid='input-conta-dig'
            />
          </div>
          <div className='p-3 md:col-span-2 md:text-left'>
            <p>CPF:</p>
            <TextInput
              type='text'
              color='light'
              defaultValue={devolucaoEmTratativa.devolucaoSelecionada?.cpf}
              value={cpfMask(devolucaoEmTratativa.devolucaoSelecionada?.cpf)}
              data-testid='input-cpf'
              disabled
            />
          </div>
          <div className='p-3 md:col-span-4 md:text-left'>
            <p>Favorecido:</p>
            <TextInput
              type='text'
              color='light'
              defaultValue={devolucaoEmTratativa.devolucaoSelecionada?.nome}
              value={devolucaoEmTratativa.devolucaoSelecionada?.nome}
              data-testid='input-favorecido'
              disabled
            />
          </div>
          <div
            className='p-3 md:col-span-4 md:text-left'
            data-testid='select-tipo-chave-pix'
          >
            <p>Tipo Chave Pix:</p>
            <Select
              placeholder='Selecione o Tipo de Chave Pix'
              isSearchable
              escapeClearsValue
              isClearable={true}
              noOptionsMessage={() => 'Sem resultados'}
              value={selectedTipoChavePixOption}
              onChange={selected => {
                const tipoSelecionado = selected?.value
                setNovoTipoChavePix(tipoSelecionado)
                const novaChave =
                  tipoSelecionado === 'CPF'
                    ? unmaskPixKey(
                        devolucaoEmTratativa?.devolucaoSelecionada?.cpf,
                        'CPF'
                      )
                    : ''
                setNovoChavePix(novaChave ?? '')
                setErros(errosAtuais =>
                  errosAtuais?.filter(
                    erro => erro.campo !== 'tipoChavePix' && erro.campo !== 'chavePix'
                  )
                )
              }}
              options={tipoChavePixOptions}
            />
            {erros?.find(e => e.campo == 'tipoChavePix' && e.erro !== 'Informe Tipo Chave Pix e Chave Pix ou preencha os dados bancários completos (Banco, Agência, Tipo Conta, Dígitos e Conta)') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='select-tipoChavePix-erro'
              >
                {erros?.find(e => e.campo == 'tipoChavePix')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div className='p-3 md:col-span-8 md:text-left'>
            <p>Chave Pix:</p>
            <TextInput
              type='text'
              color='light'
              defaultValue={devolucaoEmTratativa.dadosBancarios?.chavePix}
              value={chavePixMascarada ?? ''}
              onChange={value =>
                setNovoChavePix(
                  unmaskPixKey(value.target.value, novaTipoChavePix) ?? ''
                )
              }
              readOnly={chavePixReadonly}
              disabled={chavePixReadonly}
              data-testid='input-chave-pix'
            />
            {erros?.find(e => e.campo == 'chavePix') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='input-chave-pix-erro'
              >
                {erros?.find(e => e.campo == 'chavePix')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          {erros?.find(e => e.campo == 'tipoChavePix' && e.erro === 'Informe Tipo Chave Pix e Chave Pix ou preencha os dados bancários completos (Banco, Agência, Tipo Conta, Dígitos e Conta)') != undefined ? (
            <div className='px-3 md:col-span-12 md:text-left'>
              <p
                className='text-sm text-red-600 dark:text-red-500'
                data-testid='select-tipoChavePix-erro'
              >
                {erros?.find(e => e.campo == 'tipoChavePix')?.erro}
              </p>
            </div>
          ) : (
            <></>
          )}
        </div>

        <div
          className={
            tipoTratativa === 'Reprocessar'
              ? 'grid grid-cols-12 bg-white rounded-xl gap-5 md:gap-0 p-5 mx-5 mb-5 border border-quaternary'
              : 'grid grid-cols-12 bg-white rounded-xl gap-5 md:gap-0 p-5 mx-5 mb-5 border'
          }
        >
          <div className='p-3 flex md:col-span-12 md:text-left gap-4'>
            <Radio
              checked={tipoTratativa === 'Reprocessar'}
              onClick={() => setTipoTratativa('Reprocessar')}
              value='Reprocessar'
              name='radio-reprocessar'
              className='text-quaternary focus:ring-quaternary'
              data-testid='radio-reprocessar'
              disabled={
                devolucaoEmTratativa.devolucaoSelecionada?.tipoItemVenda ===
                'VrCredito'
              }
            />
            <span className='text-quaternary font-semibold pb-3 text-xl'>
              Reprocessar Crédito
            </span>
            {erros?.find(e => e.campo == 'tratativa') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='select-tratativa-erro'
              >
                {erros?.find(e => e.campo == 'tratativa')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div
            className='p-3 md:col-span-12 md:text-left'
            data-testid='reprocessar-info'
          >
            {devolucaoEmTratativa.devolucaoSelecionada?.tipoItemVenda ===
            'VrCredito' ? (
              <p>
                Não é possível reprocessar para compra um item que foi destinado
                para depósito em conta.
              </p>
            ) : (
              <p>
                O motivo da devolução permite que o crédito seja reprocessado.
                Verifique se há alguma ação a ser tomada, clicando em
                <b> Motivo da Devolução</b> e logo em seguida selecione esta
                opção, caso deseje que o crédito seja processado novamente.
              </p>
            )}
          </div>
          <div className='p-3 md:col-span-12 md:text-left'>
            <p>Observação:</p>
            <Textarea
              id='observacao'
              className='bg-white'
              value={observacao}
              onChange={value => setObservacao(value.target.value)}
              data-testid='input-observacao'
              disabled={
                devolucaoEmTratativa.devolucaoSelecionada?.tipoItemVenda ===
                'VrCredito'
              }
            />
            {erros?.find(e => e.campo == 'observacao') != undefined ? (
              <p
                className='mt-2 text-sm text-red-600 dark:text-red-500'
                data-testid='input-observacao-erro'
              >
                {erros?.find(e => e.campo == 'observacao')?.erro}
              </p>
            ) : (
              <></>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <>
          <div className='w-full flex justify-between'>
            <div className='flex justify-between gap-2 items-left'>
              <Button color='gray' onClick={onClose} data-testid='btn-close'>
                Cancelar
              </Button>
            </div>
            <div className='flex justify-between gap-2 items-right'>
              <Button
                color='success'
                onClick={handleTratarDevolucao}
                className='bg-quaternary'
                isProcessing={processing}
                disabled={processing}
                data-testid='btn-save'
              >
                <span>Confirmar</span>
              </Button>
            </div>
          </div>
        </>
      </Modal.Footer>
    </Modal>
  )
}

export default TratativasDevolucoesModal
 
